import type { AuthMeResponse } from '@brightlocal/api-client';
import { isSimplyListingsPackage, SIMPLY_LISTINGS_PACKAGE_IDS } from '@brightlocal/shared';

/** GTM `eventAction` values used to segment events by package group. */
export const PACKAGE_FLOWS = {
  BASIC: 'basic',
  CB_ONLY: 'cb_only'
} as const;

/** GTM `eventAction` value for package group segmentation. */
export type PackageFlow = (typeof PACKAGE_FLOWS)[keyof typeof PACKAGE_FLOWS];

/**
 * Returns the GTM `eventAction` for a given package ID.
 * Simply Listings packages map to `'cb_only'`; all others map to `'basic'`.
 */
export const getPackageFlow = (packageId: string | null | undefined) =>
  isSimplyListingsPackage(packageId) ? PACKAGE_FLOWS.CB_ONLY : PACKAGE_FLOWS.BASIC;

/** Maximum length for GTM eventDescription fields. */
const GTM_EVENT_DESCRIPTION_MAX_LENGTH = 40;

/**
 * Event fields every push sends, `null` unless the event sets them. GTM keeps the last value pushed for
 * each field, so an event that leaves one out would otherwise report another event's value.
 */
export const GTM_EMPTY_EVENT_FIELDS = {
  eventAction: null,
  eventLabel: null,
  eventDescription: null,
  eventCode: null,
  eventSource: null,
  eventType: null,
  eventProperty: null
} as const;

/**
 * Subscription fields to push for a given auth response.
 * Mirrors backend `GetGtmData::getSubscriptionData()`: Simply Listings tier is identified by
 * `package_id` (not the onboarding `flow`, which doesn't update when a user changes subscription).
 * Status is lowercased and missing values default to `null` to match the backend output.
 */
const getSubscriptionFields = (subscription?: AuthMeResponse['subscription']) => {
  if (!subscription) {
    return { paidSubscription: null, paidSubscriptionStatus: null, simplyListingStatus: null };
  }

  const status = subscription.status?.toLowerCase() ?? null;
  const isSimplyListings = SIMPLY_LISTINGS_PACKAGE_IDS.some((id) => id === subscription.packageId);

  if (isSimplyListings) {
    return { paidSubscription: null, paidSubscriptionStatus: null, simplyListingStatus: status };
  }

  return {
    paidSubscription: subscription.subscriptionName ?? null,
    paidSubscriptionStatus: status,
    simplyListingStatus: null
  };
};

/** Any serializable value accepted by the GTM data layer event */
export interface GTMEventData {
  [key: string]: unknown;
}

/** Classifies the source of a tracked error for GTM event descriptions. */
export type ErrorSource = 'ui' | 'server';

const getDataLayer = () => globalThis.window?.dataLayer;

const getTrackingIds = (authData: AuthMeResponse) => {
  const { user, customer, impersonation } = authData;

  if (impersonation) {
    return {
      userId: impersonation.impersonatorUserId,
      customerId: impersonation.impersonatorCustomerId
    };
  }

  return {
    userId: user.userId,
    customerId: customer.customerId
  };
};

/** Prefixes an error message with its source for GTM error event descriptions. */
export const formatErrorDescription = (source: ErrorSource, message: string): string => `${source} - ${message}`;

/**
 * Pushes a custom event to the GTM data layer. Event fields the event doesn't set are sent as `null`.
 * Automatically truncates string `eventDescription` to {@link GTM_EVENT_DESCRIPTION_MAX_LENGTH} characters.
 * @param eventName - The GTM event name
 * @param eventData - Optional additional properties to include with the event
 */
const pushEvent = (eventName: string, eventData?: GTMEventData) => {
  const dataLayer = getDataLayer();

  if (typeof eventData?.eventDescription === 'string') {
    eventData.eventDescription = eventData.eventDescription.slice(0, GTM_EVENT_DESCRIPTION_MAX_LENGTH);
  }

  dataLayer?.push({
    event: eventName,
    ...GTM_EMPTY_EVENT_FIELDS,
    ...eventData
  });
};

/**
 * Pushes authenticated user, customer and subscription data to the GTM data layer.
 * @param authData - Full response from the /auth/me endpoint
 */
const pushAuthData = (authData: AuthMeResponse) => {
  const dataLayer = getDataLayer();

  if (!dataLayer) {
    console.warn('GTM dataLayer is not available');
    return;
  }

  const { user, customer, subscription } = authData;

  dataLayer.push({
    ...getTrackingIds(authData),
    userRole: user.role,
    declaredCountry: customer.country,
    accountType: customer.businessType,
    clientRange: customer.managedLocationRange,
    accountCreateDate: customer.dateAdded,
    ...getSubscriptionFields(subscription)
  });
};

/**
 * Returns user, customer and subscription fields for inclusion in GTM events.
 * Pass authData when available; omit it (or pass undefined) to get all-null fields
 * e.g. before the user has authenticated.
 * @param authData - Full response from the /auth/me endpoint
 */
const buildAuthData = (authData?: AuthMeResponse, locationId: number | null = null) => ({
  ...(authData ? getTrackingIds(authData) : { userId: null, customerId: null }),
  ...getSubscriptionFields(authData?.subscription),
  accountCreateDate: authData?.customer.dateAdded ?? null,
  clientRange: authData?.customer.managedLocationRange ?? null,
  accountType: authData?.customer.businessType ?? null,
  locationId
});

/**
 * Pushes a page view event to the GTM data layer.
 * @param url - The full URL of the current page including query parameters and hash
 * @param title - The current page title
 */
const pushPageViewEvent = (url: string, title: string) => {
  pushEvent('virtual_page_view', {
    virtualURL: url,
    virtual_page_title: title
  });
};

/**
 * Clears user data from the GTM data layer and pushes a userLogout event.
 * Should be called on logout to avoid stale data.
 */
const clearAuthData = () => {
  const dataLayer = getDataLayer();

  dataLayer?.push({
    event: 'userLogout',
    userId: undefined,
    userRole: undefined,
    customerId: undefined,
    declaredCountry: undefined,
    accountType: undefined,
    clientRange: undefined,
    accountCreateDate: undefined,
    paidSubscription: undefined,
    paidSubscriptionStatus: undefined,
    simplyListingStatus: undefined
  });
};

export const GTM = {
  pushEvent,
  pushAuthData,
  buildAuthData,
  clearAuthData,
  pushPageViewEvent
};
